
import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '@clerk/clerk-react';
import {
  ProblemDetails,
  HomeworkConfig,
  SolutionDataWithId,
  DashboardData,
  AccountData,
  Transaction,
} from '../../types';
import { SAVED_CONFIG_KEY, SUPPORTED_LANGUAGES } from '../../constants';
import {
  getPresignedPostUrl,
  notifyUploadCompleted,
  pollForSolutionResult,
  uploadFileToS3,
  startProblemSolving,
  getDashboardData,
  getAccountData,
  getTransactionHistory,
  notifyPaymentCompleted,
  setTokenProvider,
} from '../../services/apiService';
import { useTranslation } from 'react-i18next';

interface ConfirmationState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
}

interface AppContextType {
  isLoading: boolean;
  problemDetails: ProblemDetails | null;
  solutionData: SolutionDataWithId | null;
  error: string | null;
  dashboardData: DashboardData | null;
  accountData: AccountData | null;
  accountTransactionsData: Transaction[];
  progress: number;
  isHelperMenuOpen: boolean;
  confirmationState: ConfirmationState;
  handleImageUpload: (file: File, dataUrl: string) => void;
  solveProblemAction: (config: HomeworkConfig, saveConfigFlag: boolean) => Promise<void>;
  navigateToHome: () => void;
  navigateToHistory: () => void;
  navigateToDashboard: () => void;
  navigateToAccount: () => void;
  navigateToAccountTransactions: () => void;
  navigateToCheckout: () => void;
  handleBackToConfig: () => void;
  handleCheckoutCompleted: (transactionId: string | null) => void;
  openHelperMenu: () => void;
  closeHelperMenu: () => void;
  requestConfirmation: (title: string, message: string, onConfirm: () => void) => void;
  closeConfirmation: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { getToken, isSignedIn } = useAuth();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [problemDetails, setProblemDetails] = useState<ProblemDetails | null>(null);
  const [solutionData, setSolutionData] = useState<SolutionDataWithId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [accountData, setAccountData] = useState<AccountData | null>(null);
  const [accountTransactionsData, setAccountTransactionsData] = useState<Transaction[]>([]);
  const [progress, setProgress] = useState(0);
  const [solutionIdForPayment, setSolutionIdForPayment] = useState<string | null>(null);
  const [isHelperMenuOpen, setIsHelperMenuOpen] = useState(false);
  const [confirmationState, setConfirmationState] = useState<ConfirmationState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const navigate = useNavigate();
  const location = useLocation();

  const navigateToHome = useCallback(() => {
    setProblemDetails(null);
    setSolutionData(null);
    setError(null);
    navigate('/app/');
  }, [navigate]);

  const handleImageUpload = useCallback(
    (file: File, dataUrl: string) => {
      const savedConfig = localStorage.getItem(SAVED_CONFIG_KEY);
      const config = savedConfig ? JSON.parse(savedConfig) : {};
      setProblemDetails({
        imageFile: file,
        imageDataUrl: dataUrl,
        grade: config.grade || '',
        language: config.language || SUPPORTED_LANGUAGES[0]?.value || 'srpski',
      });
      setError(null);
      navigate('/app/configure');
    },
    [navigate]
  );

  const solveProblemAction = useCallback(
    async (config: HomeworkConfig, saveConfigFlag: boolean) => {
      setIsLoading(true);
      setProgress(0);
      setError(null);
      setSolutionData(null);

      if (saveConfigFlag) {
        localStorage.setItem(SAVED_CONFIG_KEY, JSON.stringify(config));
      }

      if (!problemDetails || !problemDetails.imageFile) {
        setError(t('appContext.problemDetailsNotFound'));
        setIsLoading(false);
        return;
      }
      const fullProblemDetails: ProblemDetails = { ...problemDetails, ...config };
      setProblemDetails(fullProblemDetails);

      navigate('/app/solution');

      let solutionId: string | null = null;
      try {
        setProgress(5);
        const presignedData = await getPresignedPostUrl(fullProblemDetails.imageFile, config);
        solutionId = presignedData.solution_id;

        const onUploadProgress = (progressEvent: any) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(5 + percentCompleted * 0.8);
          }
        };
        await uploadFileToS3(fullProblemDetails.imageFile, presignedData, onUploadProgress);

        setProgress(85);
        await notifyUploadCompleted(solutionId);

        setProgress(90);
        await startProblemSolving(solutionId);

        const result = await pollForSolutionResult(solutionId);

        setProgress(100);
        setSolutionData(result);
      } catch (err) {
        if (solutionId && err instanceof Error && err.message.includes("nedovoljno kredita")) {
          setSolutionIdForPayment(solutionId);
          navigate('/app/checkout', { state: { from: 'solve' } });
          return;
        }
        console.error("Error solving problem:", err);
        setError(err instanceof Error ? err.message : t('appContext.solveUnknownError'));
      } finally {
        setIsLoading(false);
      }
    },
    [problemDetails, navigate, t]
  );

  const navigateWithDataFetch = useCallback(async <T,>(
    fetcher: () => Promise<T>,
    stateSetter: (data: T) => void,
    path: string,
    logMessage: string,
    errorMessage: string
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetcher();
      stateSetter(data);
      navigate(path);
    } catch (e) {
      console.error(logMessage, e);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // This effect handles deep linking and page refreshes for routes that require data.
    // It ensures that if a user lands directly on a page, the necessary data is fetched.
    const path = location.pathname;

    // Don't fetch if data already exists, or a fetch is in progress, or an error is already displayed.
    if (isLoading || error) {
      return;
    }

    if (path === '/app/dashboard' && !dashboardData && isSignedIn) {
      navigateWithDataFetch(
        getDashboardData,
        setDashboardData,
        path,
        "Failed to fetch dashboard data on load:",
        t('appContext.dashboardLoadError')
      );
    } else if (path === '/app/account' && !accountData && isSignedIn) {
      navigateWithDataFetch(
        getAccountData,
        setAccountData,
        path,
        "Failed to fetch account data on load:",
        t('appContext.accountLoadError')
      );
    }
  }, [location.pathname, dashboardData, accountData, isLoading, error, navigateWithDataFetch, isSignedIn, t]);

  useEffect(() => {
    setTokenProvider(getToken);
  }, [getToken]);

  const navigateToHistory = useCallback(() => {
    navigate('/app/history');
  }, [navigate]);

  const navigateToDashboard = useCallback(() => {
    navigateWithDataFetch(
      getDashboardData,
      setDashboardData,
      '/app/dashboard',
      "Failed to navigate to dashboard:",
      t('appContext.dashboardLoadError')
    );
  }, [navigateWithDataFetch, t]);

  const navigateToAccount = useCallback(() => {
    navigateWithDataFetch(
      getAccountData,
      setAccountData,
      '/app/account',
      "Failed to navigate to account:",
      t('appContext.accountLoadError')
    );
  }, [navigateWithDataFetch, t]);

  const navigateToAccountTransactions = useCallback(() => {
    navigateWithDataFetch(
      getTransactionHistory,
      setAccountTransactionsData,
      '/app/account/transactions',
      "Failed to navigate to transactions:",
      t('appContext.transactionsLoadError')
    );
  }, [navigateWithDataFetch, t]);

  const navigateToCheckout = useCallback(() => {
    navigate('/app/checkout');
  }, [navigate]);

  const handleBackToConfig = useCallback(() => {
    setError(null);
    navigate('/app/configure');
  }, [navigate]);

  const handleCheckoutCompleted = useCallback((transactionId: string | null) => {
    if (!transactionId) {
      toast.error(t('appContext.paymentConfirmationError'));
      setTimeout(() => navigateToDashboard(), 3000);
      return;
    }

    notifyPaymentCompleted(transactionId, solutionIdForPayment ?? undefined)
      .then(() => {
        toast.success(t('appContext.paymentSuccess'));
        setSolutionIdForPayment(null);
        setTimeout(() => navigateToDashboard(), 1000);
      })
      .catch(err => {
        console.error("Failed to verify payment with backend:", err);
        toast.error(t('appContext.creditAdditionError'));
        setSolutionIdForPayment(null);
        setTimeout(() => navigateToDashboard(), 3000);
      });
  }, [navigateToDashboard, solutionIdForPayment, t]);

  const openHelperMenu = useCallback(() => setIsHelperMenuOpen(true), []);
  const closeHelperMenu = useCallback(() => setIsHelperMenuOpen(false), []);

  const requestConfirmation = useCallback((title: string, message: string, onConfirm: () => void) => {
    setConfirmationState({ isOpen: true, title, message, onConfirm });
  }, []);

  const closeConfirmation = useCallback(() => {
    setConfirmationState(prevState => ({ ...prevState, isOpen: false }));
  }, []);

  const contextValue = {
    isLoading,
    problemDetails,
    solutionData,
    error,
    dashboardData,
    accountData,
    accountTransactionsData,
    progress,
    isHelperMenuOpen,
    confirmationState,
    handleImageUpload,
    solveProblemAction,
    navigateToHome,
    navigateToHistory,
    navigateToDashboard,
    navigateToAccount,
    navigateToAccountTransactions,
    navigateToCheckout,
    handleBackToConfig,
    handleCheckoutCompleted,
    openHelperMenu,
    closeHelperMenu,
    requestConfirmation,
    closeConfirmation,
  };

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
